import React, { useState } from 'react';
import TemplatePreview from './TemplatePreview';
import WhatsAppLink from './WhatsAppLink';

/**
 * Componente para visualizar detalhes de um template
 * Permite copiar e compartilhar o template
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.template - Dados do template
 * @param {string} props.previewMessage - Mensagem processada para prévia
 * @param {Function} props.onEdit - Função para editar o template
 * @param {Function} props.onClose - Função para fechar a visualização
 * @returns {JSX.Element} Componente React
 */
const TemplateDetail = ({ template, previewMessage, onEdit, onClose }) => {
  const [showShareOptions, setShowShareOptions] = useState(false);
  
  // Mapear tipos para nomes amigáveis
  const getTypeName = (type) => {
    const typeNames = {
      confirmacao: 'Confirmação',
      lembrete: 'Lembrete',
      agradecimento: 'Agradecimento',
      cancelamento: 'Cancelamento',
      reagendamento: 'Reagendamento',
      aniversario: 'Aniversário',
      promocao: 'Promoção'
    };
    
    return typeNames[type] || type;
  };
  
  // Mapear tipos para cores
  const getTypeColor = (type) => {
    const typeColors = {
      confirmacao: 'bg-green-100 text-green-800 border-green-200',
      lembrete: 'bg-blue-100 text-blue-800 border-blue-200',
      agradecimento: 'bg-purple-100 text-purple-800 border-purple-200',
      cancelamento: 'bg-red-100 text-red-800 border-red-200',
      reagendamento: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      aniversario: 'bg-pink-100 text-pink-800 border-pink-200',
      promocao: 'bg-indigo-100 text-indigo-800 border-indigo-200'
    };
    
    return typeColors[type] || 'bg-gray-100 text-gray-800 border-gray-200';
  };
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className={`text-lg font-medium px-3 py-1.5 rounded-md ${getTypeColor(template.type)}`}>
          {getTypeName(template.type)}
        </h3>
        
        <div className="flex items-center space-x-1">
          <button
            onClick={onEdit}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-full"
            aria-label="Editar"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 0L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:bg-gray-100 rounded-full"
            aria-label="Fechar"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Mensagem</h4>
        <div className="bg-white p-3 rounded border border-gray-200 whitespace-pre-line text-sm">
          {template.message}
        </div>
      </div>
      
      <TemplatePreview message={previewMessage} />
      
      <div className="flex flex-col space-y-2">
        <button
          onClick={() => setShowShareOptions(!showShareOptions)}
          className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          {showShareOptions ? 'Ocultar Opções de Compartilhamento' : 'Compartilhar'}
        </button>
      </div>
      
      {showShareOptions && (
        <WhatsAppLink message={previewMessage} />
      )}
    </div>
  );
};

export default TemplateDetail;
